/**
 * Pure helpers for the floating widget icon position. Lives next to
 * FeedbackWidget.tsx because the consuming apps copy this directory
 * verbatim. No React imports — keeps the unit tests trivial.
 *
 * Phase 14 Plan-1: drag + remembered position. Storage key locked in
 * .planning/phases/14-widget-polish/DESIGN-DECISIONS.md section 1.
 */

export const POSITION_STORAGE_KEY = "180d-feedback-widget-position";
export const VIEWPORT_GUTTER = 8; // px — closest the icon can sit to any edge
export const SNAP_THRESHOLD = 32; // px — drop within this distance → snap to edge

export type Edge = "tl" | "tr" | "bl" | "br" | "free";

export type SavedPosition = {
  x: number;
  y: number;
  edge: Edge;
  v: 1;
};

export type Viewport = {
  width: number;
  height: number;
};

export type Size = {
  width: number;
  height: number;
};

/**
 * Clamp a candidate position so the icon never sits closer than
 * VIEWPORT_GUTTER pixels to any viewport edge.
 */
export function clampPosition(
  pos: { x: number; y: number },
  iconSize: Size,
  viewport: Viewport,
): { x: number; y: number } {
  const minX = VIEWPORT_GUTTER;
  const minY = VIEWPORT_GUTTER;
  const maxX = viewport.width - iconSize.width - VIEWPORT_GUTTER;
  const maxY = viewport.height - iconSize.height - VIEWPORT_GUTTER;
  return {
    x: Math.min(Math.max(pos.x, minX), Math.max(minX, maxX)),
    y: Math.min(Math.max(pos.y, minY), Math.max(minY, maxY)),
  };
}

/**
 * If a position is within SNAP_THRESHOLD of any edge, snap to that
 * edge with the gutter applied. Otherwise return a "free" position.
 * Used on pointerup so a user dragging close to a corner gets a
 * clean alignment instead of a near-miss.
 */
export function snapToEdge(
  pos: { x: number; y: number },
  iconSize: Size,
  viewport: Viewport,
): SavedPosition {
  const rightEdge = viewport.width - iconSize.width - VIEWPORT_GUTTER;
  const bottomEdge = viewport.height - iconSize.height - VIEWPORT_GUTTER;

  const nearLeft = pos.x <= VIEWPORT_GUTTER + SNAP_THRESHOLD;
  const nearRight = pos.x >= rightEdge - SNAP_THRESHOLD;
  const nearTop = pos.y <= VIEWPORT_GUTTER + SNAP_THRESHOLD;
  const nearBottom = pos.y >= bottomEdge - SNAP_THRESHOLD;

  if (nearTop && nearLeft) return { x: VIEWPORT_GUTTER, y: VIEWPORT_GUTTER, edge: "tl", v: 1 };
  if (nearTop && nearRight) return { x: rightEdge, y: VIEWPORT_GUTTER, edge: "tr", v: 1 };
  if (nearBottom && nearLeft) return { x: VIEWPORT_GUTTER, y: bottomEdge, edge: "bl", v: 1 };
  if (nearBottom && nearRight) return { x: rightEdge, y: bottomEdge, edge: "br", v: 1 };

  return { ...clampPosition(pos, iconSize, viewport), edge: "free", v: 1 };
}

/**
 * On window resize: take a saved position that may now be outside
 * the new viewport and bring it back into the closest equivalent
 * spot in the same conceptual quadrant.
 */
export function reflowPosition(
  saved: SavedPosition,
  iconSize: Size,
  viewport: Viewport,
): SavedPosition {
  const rightEdge = viewport.width - iconSize.width - VIEWPORT_GUTTER;
  const bottomEdge = viewport.height - iconSize.height - VIEWPORT_GUTTER;

  switch (saved.edge) {
    case "tl":
      return { x: VIEWPORT_GUTTER, y: VIEWPORT_GUTTER, edge: "tl", v: 1 };
    case "tr":
      return { x: rightEdge, y: VIEWPORT_GUTTER, edge: "tr", v: 1 };
    case "bl":
      return { x: VIEWPORT_GUTTER, y: bottomEdge, edge: "bl", v: 1 };
    case "br":
      return { x: rightEdge, y: bottomEdge, edge: "br", v: 1 };
    case "free":
    default:
      return { ...clampPosition({ x: saved.x, y: saved.y }, iconSize, viewport), edge: "free", v: 1 };
  }
}

/**
 * Default position for a never-dragged widget: bottom-right corner
 * with the gutter. Matches the legacy hardcoded right: 20, bottom: 20
 * shape closely enough that returning users see the icon where it's
 * always been.
 */
export function defaultPosition(iconSize: Size, viewport: Viewport): SavedPosition {
  return {
    x: viewport.width - iconSize.width - VIEWPORT_GUTTER,
    y: viewport.height - iconSize.height - VIEWPORT_GUTTER,
    edge: "br",
    v: 1,
  };
}

/**
 * Read saved position from localStorage. Returns null if no save,
 * the save is malformed, or storage access throws (Safari private
 * mode, cross-origin iframe with restricted storage, etc.).
 */
export function readSavedPosition(storage: Pick<Storage, "getItem"> | null): SavedPosition | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.x === "number" &&
      typeof parsed.y === "number" &&
      typeof parsed.edge === "string" &&
      ["tl", "tr", "bl", "br", "free"].includes(parsed.edge) &&
      parsed.v === 1
    ) {
      return parsed as SavedPosition;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Write to localStorage. Swallow errors so a storage failure can
 * never crash the widget; the position simply won't persist.
 */
export function writeSavedPosition(
  storage: Pick<Storage, "setItem"> | null,
  pos: SavedPosition,
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
    return true;
  } catch {
    return false;
  }
}

/**
 * Wipe the saved position. Right-click menu → "Reset position" calls
 * this so the next mount goes back to the default corner.
 */
export function clearSavedPosition(storage: Pick<Storage, "removeItem"> | null): void {
  if (!storage) return;
  try {
    storage.removeItem(POSITION_STORAGE_KEY);
  } catch {
    // ignore
  }
}
